import { Icon } from "@iconify/react";
import Dialog from "@mui/material/Dialog";
import LinearProgress, {
  linearProgressClasses,
} from "@mui/material/LinearProgress";
import { styled } from "@mui/material/styles";
import _ from "lodash";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { format } from "date-fns";
import { Action, CTADropdown } from "../../components/shared/Dropdown";
import {
  Box,
  ButtonPrimary,
  ButtonPrimary1,
  ButtonSize,
  Center,
  HStack,
  VStack,
} from "../../components/utils";
import { AddOrEditGrant } from "../esopOverview/AddOrEditGrant";

import AlertDialog from "../../components/shared/AlertDialog";
import RevertResignationDialog from "../../components/shared/RevertResignationDialog";
import {
  Employee,
  EmployementStatus,
  RevertResignation,
  RevokeAccessRequest,
} from "../../types/Employee";

import { jwtAdminForEmployee } from "../../api/Esop";
import StatusIndicator from "../../components/shared/StatusIndicator";
import { InitiateResignation } from "../../modals/InitiateResignation";
import {
  useDeleteEmployee,
  useEmployeePageDetails,
  useEmployeeTransactionDetails,
  useGenerateEmployeeCredentials,
  useGetResignationDetails,
  useRevokeOrEnableAccess,
  useTransferEmployeeAccess,
} from "../../queries/employees";
import { usePauseVesting } from "../../queries/vest";
import { useGetGrantLetterSettings } from "../../queries/siteSettings";
import { useError } from "../../store/errorStore";
import { usePermissionStore } from "../../store/permissionStore";
import { useSignaturePadDialog } from "../../store/useDialogStore";
import {
  EmployeePageDetails,
  PauseVestingDto,
} from "../../types/VestingTemplate";
import { determineUserAccessToResource } from "../../utils/auth";
import { formatCurrency, getCurrencyType } from "../../utils/currencyFormatter";
import useIsMobile from "../../utils/detectDevice";
import {
  Action as DefaultAction,
  Resource,
} from "../../utils/interfaces/Companies";
import { getFormattedValue } from "../../utils/string";
import { Input } from "../../components/shared/InputField";
import { SwitchButton } from "../../components/shared/SwitchButton";
import { formatDisplayDate } from "../../utils/date";
import {
  closeEmployeePreviewFrame,
  listenForEmployeePreviewRenewal,
} from "../../utils/employeePreviewFrame";
import { EmployeePortalPreview } from "../../components/EmployeePortalPreview";

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 5,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor:
      theme.palette.grey[theme.palette.mode === "light" ? 200 : 800],
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: theme.palette.mode === "light" ? "#66D497" : "#F5F5F5",
  },
}));

type EmployeeHeaderTypes = {
  activeTab: string;
  setActiveTab: Function;
} & React.HTMLAttributes<HTMLElement>;

const EmployeeHeader = (props: EmployeeHeaderTypes) => {
  const currency = getCurrencyType();
  const { id } = useParams();
  const _id = id ?? "";
  const navigate = useNavigate();

  const errorMessage = useError();
  const {
    data: _employeePageDetail,
    refetch,
    isLoading,
  } = useEmployeePageDetails(_id);
  const { data: transactionDetails } = useEmployeeTransactionDetails(_id);
  const employeePageDetail = _employeePageDetail as EmployeePageDetails;
  const { data: grantSettings } = useGetGrantLetterSettings();
  const dateForLapsing = grantSettings?.dateForLapsing || "lastWorkingDay";

  const isPendingResignation = useMemo(() => {
    if (employeePageDetail?.isResignationSchedule) return false;
    if (employeePageDetail?.status !== EmployementStatus.EMPLOYED) return false;
    const now = new Date();
    const resignDate = employeePageDetail?.resignationDate
      ? new Date(employeePageDetail.resignationDate)
      : null;
    const lastDay = employeePageDetail?.lastDay
      ? new Date(employeePageDetail.lastDay)
      : null;
    if (resignDate && lastDay) return resignDate <= now && lastDay <= now;
    if (resignDate && !lastDay && dateForLapsing === "resignationDate")
      return resignDate <= now;
    return false;
  }, [employeePageDetail, dateForLapsing]);

  useEffect(() => {
    refetch();
  }, []);
  const { permission } = usePermissionStore();
  const isUserAdmin = useMemo(
    () =>
      determineUserAccessToResource(
        permission?.aclList || [],
        Resource.All,
        DefaultAction.All
      ),
    [permission]
  );

  const [dialog, setDialog] = useState<{
    open: boolean;
    user?: Employee | EmployeePageDetails;
    mode?: "Edit" | "Add" | "Clone" | "Delete";
    message?: string;
    primaryAction?: () => void;
  }>({ open: false, user: undefined, mode: "Add" });
  const [employeeAccessDialog, setEmployeeAccessDialog] = useState<{
    open: boolean;
    personalEmail?: string;
    regenerateCredentials?: boolean;
  }>({ open: false, personalEmail: "" });
  const [employeeDetailsDialog, setEmployeeDetailsDialog] = useState<{
    open: boolean;
    previewAccessToken: string;
    employeeId: string;
  }>({ open: false, previewAccessToken: "", employeeId: "" });
  const employeePreviewFrame = useRef<HTMLIFrameElement>(null);
  const closingEmployeePreview = useRef(false);
  useEffect(() => {
    if (!employeeDetailsDialog.open || !employeeDetailsDialog.employeeId)
      return undefined;
    return listenForEmployeePreviewRenewal(
      () => employeePreviewFrame.current,
      employeeDetailsDialog.employeeId,
      async (employeeId) => (await jwtAdminForEmployee(employeeId)).data,
      () => closingEmployeePreview.current
    );
  }, [employeeDetailsDialog.open, employeeDetailsDialog.employeeId]);
  const { activeTab, setActiveTab, ...rest } = props;
  const { mutate: deleteEmp, isLoading: deletingEmployee } =
    useDeleteEmployee();
  const { mutate: revokeOrEnableAccess, isLoading: revokingOrEnablingAccess } =
    useRevokeOrEnableAccess();
  const { mutate: pauseVesting, isLoading: resumeVesting } = usePauseVesting();
  const { mutate: transferAccess, isLoading: transferringAccess } =
    useTransferEmployeeAccess();
  const [open, setOpen] = useState<boolean>(false);
  const [initiationOpen, setInitiationOpen] = useState<boolean>(false);
  const [revertResignationDialog, setRevertResignationDialog] = useState<{
    open: boolean;
    revertResignation: RevertResignation;
  }>({
    open: false,
    revertResignation: {
      employeeIds: [],
      note: "",
      revertAccessBackToPrimaryEmailId: false,
      sendEmailForVestings: false,
      vestAsPerToday: true,
      resumeFutureVestings: false,
    },
  });
  const showActive =
    employeePageDetail?.showActiveGrantedOptionsToAdmin ?? false;
  const employeeGrantedOption = showActive
    ? employeePageDetail?.activeGrantedOptions ?? 0
    : employeePageDetail?.grantedOptions ?? 0;
  const employeeVestedOption = showActive
    ? employeePageDetail?.netVestedOptions ?? 0
    : employeePageDetail?.vestedOptions ?? 0;
  const employeeExercisedOption = employeePageDetail?.exercisedOptions ?? 0;
  const employeeSharesReceived = employeePageDetail?.sharesReceived ?? 0;

  const employeeSoldShares = _.sumBy(
    transactionDetails,
    (t) => t.noOfSoldShares
  );
  const employeeBalancedShares = employeeSharesReceived - employeeSoldShares;
  const vestedFactor = employeeVestedOption / employeeGrantedOption;
  const tabs = ["Grant Details", "Profile", "Activity Log"];

  const { data: resignationDetails } = useGetResignationDetails(_id);

  if (
    resignationDetails?.initiationDetails?.resignationDate &&
    !employeePageDetail?.userHasLimitedAccessForEmployee
  )
    tabs.push("Resignation");

  const moreActions = [
    {
      name: employeePageDetail?.isPausedVesting
        ? "Resume Vesting"
        : "Pause Vesting",
      disabled:
        employeePageDetail?.userHasLimitedAccessForEmployee ||
        employeePageDetail?.status === EmployementStatus.RESIGNED ||
        resignationDetails?.initiationDetails.grantTableDetails.length === 0 ||
        !isUserAdmin ||
        employeePageDetail?.grantedOptions <= 0,
      tooltipText: employeePageDetail?.userHasLimitedAccessForEmployee
        ? "You have limited access for this employee"
        : !isUserAdmin
        ? "Admin access required"
        : employeePageDetail?.status === EmployementStatus.RESIGNED
        ? "Employee has already resigned"
        : employeePageDetail?.grantedOptions <= 0
        ? "Employee has no granted options"
        : resignationDetails?.initiationDetails.grantTableDetails.length === 0
        ? "No grants in granted state"
        : undefined,
    },
    {
      name: "Initiate Resignation",
      disabled:
        employeePageDetail?.status !== EmployementStatus.EMPLOYED ||
        !isUserAdmin ||
        employeePageDetail?.userHasLimitedAccessForEmployee ||
        Boolean(employeePageDetail?.resignationDate),
      tooltipText: employeePageDetail?.userHasLimitedAccessForEmployee
        ? "You have limited access for this employee"
        : !isUserAdmin
        ? "Admin access required"
        : employeePageDetail?.status === EmployementStatus.RESIGNED
        ? "Employee is already resigned"
        : employeePageDetail?.resignationDate
        ? "Resignation has already been initiated"
        : employeePageDetail?.status !== EmployementStatus.EMPLOYED
        ? "Employee is not currently employed"
        : undefined,
    },
    {
      name: `${
        employeePageDetail?.deactivate !== true
          ? "Revoke Access"
          : "Enable Access"
      }`,
      disabled:
        !isUserAdmin ||
        employeePageDetail?.userHasLimitedAccessForEmployee ||
        !employeePageDetail?.isLoginProvided,
      tooltipText: employeePageDetail?.userHasLimitedAccessForEmployee
        ? "You have limited access for this employee"
        : !isUserAdmin
        ? "Admin access required"
        : !employeePageDetail?.isLoginProvided
        ? "Credentials have not been generated for this employee"
        : undefined,
    },
    {
      name: "Transfer access to Personal Email",
      disabled:
        !isUserAdmin ||
        employeePageDetail?.userHasLimitedAccessForEmployee ||
        employeePageDetail?.status !== EmployementStatus.RESIGNED ||
        !determineUserAccessToResource(
          permission?.aclList || [],
          Resource.Employee,
          DefaultAction.Edit
        ),
      tooltipText: employeePageDetail?.userHasLimitedAccessForEmployee
        ? "You have limited access for this employee"
        : !isUserAdmin
        ? "Admin access required"
        : employeePageDetail?.status !== EmployementStatus.RESIGNED
        ? "Employee must be resigned to transfer access"
        : !determineUserAccessToResource(
            permission?.aclList || [],
            Resource.Employee,
            DefaultAction.Edit
          )
        ? "Insufficient permissions to edit employee"
        : undefined,
    },
    {
      name: "Revert Resignation",
      disabled:
        !isUserAdmin ||
        employeePageDetail?.userHasLimitedAccessForEmployee ||
        employeePageDetail?.status !== EmployementStatus.RESIGNED ||
        !determineUserAccessToResource(
          permission?.aclList || [],
          Resource.Employee,
          DefaultAction.Edit
        ),
      tooltipText: employeePageDetail?.userHasLimitedAccessForEmployee
        ? "You have limited access for this employee"
        : !isUserAdmin
        ? "Admin access required"
        : employeePageDetail?.status !== EmployementStatus.RESIGNED
        ? "Employee must be resigned to revert resignation"
        : !determineUserAccessToResource(
            permission?.aclList || [],
            Resource.Employee,
            DefaultAction.Edit
          )
        ? "Insufficient permissions to edit employee"
        : undefined,
    },
    {
      name: "Preview Employee View",
      disabled: !isUserAdmin,
      tooltipText: !isUserAdmin ? "Admin access required" : undefined,
    },
    {
      name: "Delete Employee",
      disabled:
        employeePageDetail?.grantedOptions !== 0 ||
        (employeePageDetail?.isLoginProvided !== null
          ? employeePageDetail.isLoginProvided
          : false) ||
        !isUserAdmin ||
        employeePageDetail?.userHasLimitedAccessForEmployee ||
        !determineUserAccessToResource(
          permission?.aclList || [],
          Resource.Employee,
          DefaultAction.Delete
        ),
      tooltipText:
        employeePageDetail?.grantedOptions !== 0
          ? "Employee has active grants and cannot be deleted"
          : employeePageDetail?.isLoginProvided
          ? "Employee has generated credentials and cannot be deleted"
          : employeePageDetail?.userHasLimitedAccessForEmployee
          ? "You have limited access for this employee"
          : !isUserAdmin
          ? "Admin access required"
          : !determineUserAccessToResource(
              permission?.aclList || [],
              Resource.Employee,
              DefaultAction.Delete
            )
          ? "Insufficient permissions to delete employee"
          : undefined,
    },
  ];

  const {
    mutate: generateEmployeeCredentials,
    isLoading: generateCredentialLoading,
  } = useGenerateEmployeeCredentials();
  function genCred() {
    generateEmployeeCredentials(
      {
        employeeIds: [_id],
        regenerateExisting: true,
        sendCredentialsGenerationEmail: true,
      },
      {
        onSuccess: () => {
          toast("Credentials Generated successfully!", {
            type: "success",
            autoClose: 2000,
          });
          setDialog({ open: false });
        },
        onError: (err: any) => {
          errorMessage.setMessage(err.response.data.reason);
          toast(err.response.data.reason, { type: "error", autoClose: 2000 });
        },
      }
    );
  }
  function deleteEmployee() {
    if (employeePageDetail?.grantedOptions === 0) {
      deleteEmp([_id], {
        onSuccess: () => {
          toast("Employee deleted successfully!", {
            type: "success",
            autoClose: 2000,
          });
          setDialog({ open: false });
          navigate("/options/allEmployees");
        },
        onError: (err: any) => {
          errorMessage.setMessage(err.response.data.reason);
          toast(err.response.data.reason, { type: "error", autoClose: 2000 });
          setDialog({ open: false });
        },
      });
    } else {
      toast("Employee can not be deleted as employee has grants", {
        type: "error",
        autoClose: 5000,
      });
      setDialog({ open: false });
    }
  }
  function revokeAccess() {
    const revokeAccessDto: RevokeAccessRequest = {
      empId: _id,
      isDeactived: true,
    };
    revokeOrEnableAccess(revokeAccessDto, {
      onSuccess: () => {
        toast("Employee is successfully disabled", {
          type: "success",
          autoClose: 2000,
        });
        setDialog({ open: false });
      },
      onError: (err: any) => {
        errorMessage.setMessage(err.response.data.reason);
        toast(err.response.data.reason, { type: "error", autoClose: 2000 });
      },
    });
  }
  function pauseVestingFunction() {
    const pauseVestingDto: PauseVestingDto = {
      employeeId: employeePageDetail.id,
      isPausedVesting: !employeePageDetail.isPausedVesting,
    };
    pauseVesting(pauseVestingDto, {
      onSuccess: (res) => {
        toast(res.data, {
          type: "success",
          autoClose: 2000,
        });
        setDialog({ open: false });
      },
      onError: (err: any) => {
        errorMessage.setMessage(err.response);
        toast(err.response, { type: "error", autoClose: 2000 });
      },
    });
  }

  function enableAccess() {
    const revokeAccessDto: RevokeAccessRequest = {
      empId: _id,
      isDeactived: false,
    };
    revokeOrEnableAccess(revokeAccessDto, {
      onSuccess: () => {
        toast("Employee is successfully enabled", {
          type: "success",
          autoClose: 2000,
        });
        setDialog({ open: false });
      },
      onError: (err: any) => {
        errorMessage.setMessage(err.response.data.reason);
        toast(err.response.data.reason, { type: "error", autoClose: 2000 });
      },
    });
  }
  function redirectToEmployeePortal() {
    jwtAdminForEmployee(_id).then(
      (data) => {
        const jwtAccessToken = data.data;
        setEmployeeDetailsDialog({
          open: true,
          previewAccessToken: jwtAccessToken,
          employeeId: _id,
        });
      },
      (err) => {
        toast(err.response.data.reason, { type: "error", autoClose: 2000 });
      }
    );
  }
  function closeEmployeePreview() {
    if (closingEmployeePreview.current) return;
    closingEmployeePreview.current = true;
    closeEmployeePreviewFrame(employeePreviewFrame.current, () => {
      closingEmployeePreview.current = false;
      setEmployeeDetailsDialog({
        open: false,
        previewAccessToken: "",
        employeeId: "",
      });
    });
  }
  const { state: showSignaturePad, setState: setShowSignaturePad } =
    useSignaturePadDialog();
  function handleAction(user: EmployeePageDetails, action: Action) {
    if (action.disabled) return;
    switch (action.name) {
      case "Initiate Resignation": {
        setInitiationOpen(!initiationOpen);
        break;
      }
      case "Pause Vesting": {
        setDialog({
          open: true,
          user,
          message: "Are you sure you want to Pause Vesting ?",
          primaryAction: pauseVestingFunction,
        });
        break;
      }
      case "Resume Vesting": {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        let message =
          "Are you sure you want to Resume Vesting? All paused options will be vested up until today!";

        const lastDay = employeePageDetail?.lastDay;
        if (lastDay) {
          const lwd = new Date(lastDay);
          lwd.setHours(0, 0, 0, 0);

          if (lwd < today) {
            const formattedLWD = format(lwd, "dd-MM-yyyy");
            message = `Are you sure you want to Resume Vesting? All paused options will be vested up until last working date i.e, ${formattedLWD}`;
          }
        }
        setDialog({
          open: true,
          user,
          message,
          primaryAction: pauseVestingFunction,
        });
        break;
      }
      case "Revoke Access": {
        setDialog({
          open: true,
          user,
          message: "Are you sure you want to Revoke Access ?",
          primaryAction: revokeAccess,
        });
        break;
      }
      case "Enable Access": {
        setDialog({
          open: true,
          user,
          message: "Are you sure you want to Enable Access ?",
          primaryAction: enableAccess,
        });
        break;
      }
      case "Send Summary in Email": {
        setDialog({
          open: true,
          user,
          message: "Are you sure you want to Send Summary in Email ? ",
        });
        break;
      }
      case "Delete Employee": {
        setDialog({
          open: true,
          user,
          message: "Are you sure you want to delete the Employee ?",
          mode: "Delete",
          primaryAction: deleteEmployee,
        });
        break;
      }
      case "Transfer access to Personal Email": {
        setEmployeeAccessDialog({
          open: true,
          personalEmail: employeePageDetail?.personalEmail,
          regenerateCredentials: false,
        });
        break;
      }
      case "Revert Resignation": {
        setRevertResignationDialog({
          open: true,
          revertResignation: {
            employeeIds: [_id],
            note: "",
            revertAccessBackToPrimaryEmailId: false,
            sendEmailForVestings: false,
            vestAsPerToday: true,
            resumeFutureVestings: false,
          },
        });
        break;
      }
      case "Preview Employee View": {
        redirectToEmployeePortal();
        break;
      }
      default: {
        break;
      }
    }
  }
  const [show, setShow] = useState(false);
  const { isMobile } = useIsMobile();
  return (
    <Box
      className={`${isLoading ? "loading" : ""} p-6 pb-0 bg-white rounded-lg"`}
    >
      <Box
        className={`flex ${isMobile ? "flex-col" : "flex-row"}  w-full gap-2`}
      >
        <VStack className="items-center">
          <Box className="items-center rounded bg-slate-501">
            <Center className="relative w-40 h-40 bg-gray-100 ">
              {!employeePageDetail?.imageURL ? (
                <>
                  <Center className="flex w-40 h-40 rounded-full ">
                    <p className="text-base font-bold ">
                      {employeePageDetail?.employeeName.charAt(0)}
                    </p>
                  </Center>
                  <StatusIndicator
                    className="absolute"
                    status={
                      employeePageDetail?.isResignationSchedule
                        ? "RESIGNATION SCHEDULED"
                        : isPendingResignation
                        ? "PENDING RESIGNATION"
                        : employeePageDetail?.status ?? ""
                    }
                  />
                </>
              ) : (
                <>
                  {" "}
                  <Center className="w-fit h-fit">
                    <img
                      className="max-h-36 aspect-square"
                      src={employeePageDetail?.imageURL}
                    />
                  </Center>
                  <StatusIndicator
                    className="absolute"
                    status={
                      employeePageDetail?.isResignationSchedule
                        ? "RESIGNATION SCHEDULED"
                        : isPendingResignation
                        ? "PENDING RESIGNATION"
                        : employeePageDetail?.status ?? ""
                    }
                  />
                </>
              )}
            </Center>
          </Box>
          <HStack className="flex items-center justify-center mt-2">
            <StatusIndicator
              className=""
              status={
                employeePageDetail?.isResignationSchedule
                  ? "RESIGNATION SCHEDULED"
                  : isPendingResignation
                  ? "PENDING RESIGNATION"
                  : employeePageDetail?.status ?? ""
              }
            />
            <Box className="text-sm">
              <p className="capitalize">
                {employeePageDetail?.isResignationSchedule
                  ? "Resignation Scheduled"
                  : isPendingResignation
                  ? "Pending Resignation"
                  : employeePageDetail?.status.toLowerCase()}
              </p>
            </Box>
          </HStack>
        </VStack>
        <Box className="w-full">
          <HStack className="flex-wrap justify-between gap-4">
            <VStack className="justify-between gap-4">
              <VStack>
                <Box className="flex flex-row items-center space-x-2">
                  <Box className="flex flex-row items-center min-w-min max-w-[450px] font-semibold text-lg text-gray-600">
                    <p>{employeePageDetail?.employeeName}</p>
                    <p className="ml-2">
                      {employeePageDetail?.employeeIdentification}
                    </p>
                  </Box>
                  {employeePageDetail?.isLoginProvided === true ? (
                    <Box className="p-1 rounded text-xxs1 bg-green-301 text-green-401">
                      <p> Credentials Generated </p>
                    </Box>
                  ) : (
                    <Box className="p-1 text-red-400 bg-red-100 rounded text-xxs1">
                      <p>Credentials not generated </p>
                    </Box>
                  )}
                </Box>
                <Box className="flex flex-row space-x-4">
                  <Box className="flex flex-row items-center space-x-1 text-xs text-gray-201">
                    <Box className=" text-xxs1">
                      <Icon icon="carbon:user-filled" />
                    </Box>
                    <Box className="mr-1 text-xxs1 justify-bottom">
                      <p>{employeePageDetail?.employmentType}</p>
                    </Box>
                  </Box>
                  <Box className="flex flex-row items-center space-x-1 text-xs text-gray-201">
                    <Box className="text-xxs1">
                      <Icon icon="icon-park-solid:handbag" />
                    </Box>
                    <Box className="mr-1 text-xxs1">
                      <p>{employeePageDetail?.designation}</p>
                    </Box>
                  </Box>
                  <Box className="flex flex-row items-center space-x-1 text-xs text-gray-201">
                    <Box className=" text-xxs1">
                      <Icon icon="ci:location" />
                    </Box>
                    <Box className="mr-1 text-xxs1">
                      <p>{employeePageDetail?.residentStatus}</p>
                    </Box>
                  </Box>
                </Box>
              </VStack>
              <VStack className="gap-4">
                <Box className="flex flex-row space-x-4">
                  <VStack className="p-2 text-xs border border-dashed rounded xl:w-36 lg:w-28 md:w-20 sm:w-16">
                    <Box className="p-1 pb-0 font-semibold text-black-dark text-xs3">
                      <p>
                        {getFormattedValue(
                          employeeGrantedOption,
                          currency,
                          employeePageDetail?.isFractional
                        )}
                      </p>
                    </Box>
                    <Box className="p-1 mr-2 text-xxs1 text-gray-201">
                      {showActive ? "Active Granted" : "Granted"}
                    </Box>
                  </VStack>
                  <VStack className="p-2 text-xs border border-dashed rounded xl:w-36 lg:w-28 md:w-20 sm:w-16">
                    <Box className="p-1 pb-0 font-semibold text-black-dark text-xs3">
                      <p>
                        {getFormattedValue(
                          employeeVestedOption,
                          currency,
                          employeePageDetail?.isFractional
                        )}
                      </p>
                    </Box>
                    <Box className="p-1 mr-2 text-xxs1 text-gray-201">
                      {showActive ? "Net Vested" : "Vested"}
                    </Box>
                  </VStack>
                  <VStack className="p-2 text-xs border border-dashed rounded xl:w-36 lg:w-28 md:w-20 sm:w-16">
                    <Box className="p-1 pb-0 font-semibold text-black-dark text-xs3">
                      <p>
                        {getFormattedValue(
                          employeeExercisedOption,
                          currency,
                          employeePageDetail?.isFractional
                        )}
                      </p>
                    </Box>
                    <Box className="p-1 mr-2 text-xxs1 text-gray-201">
                      Exercised
                    </Box>
                  </VStack>
                </Box>
                <Box className="flex flex-row space-x-4">
                  <VStack className="p-2 text-xs border border-dashed rounded xl:w-36 lg:w-28 md:w-20 sm:w-16">
                    <Box className="p-1 pb-0 font-semibold text-black-dark text-xs3">
                      <p>
                        {getFormattedValue(
                          employeeSharesReceived,
                          currency,
                          employeePageDetail?.isFractional
                        )}
                      </p>
                    </Box>
                    <Box className="p-1 mr-2 text-xxs1 text-gray-201">
                      Shares received
                    </Box>
                  </VStack>
                  <VStack className="p-2 text-xs border border-dashed rounded xl:w-36 lg:w-28 md:w-20 sm:w-16">
                    <Box className="p-1 pb-0 font-semibold text-black-dark text-xs3">
                      <p>
                        {getFormattedValue(
                          employeeSoldShares,
                          currency,
                          employeePageDetail?.isFractional
                        )}
                      </p>
                    </Box>
                    <Box className="p-1 mr-2 text-xxs1 text-gray-201">
                      Shares sold
                    </Box>
                  </VStack>
                  <VStack className="p-2 text-xs border border-dashed rounded xl:w-36 lg:w-28 md:w-20 sm:w-16">
                    <Box className="p-1 pb-0 font-semibold text-black-dark text-xs3">
                      <p>
                        {getFormattedValue(
                          employeeBalancedShares,
                          currency,
                          employeePageDetail?.isFractional
                        )}
                      </p>
                    </Box>
                    <Box className="p-1 mr-2 text-xxs1 text-gray-201">
                      Balance shares
                    </Box>
                  </VStack>
                </Box>
                {employeePageDetail?.showPPSToEmployees ? (
                  <Box className="flex flex-col ">
                    <Box className="flex flex-row space-x-4">
                      <VStack className="p-2 text-xs border border-dashed rounded xl:min-w-36 lg:min-w-28 md:min-w-20 sm:min-w-16">
                        <Box className="p-1 pb-0 font-semibold text-black-dark text-xs3">
                          <p>
                            {formatCurrency(
                              employeePageDetail?.ppsforEmployee || 0,
                              currency
                            )}
                          </p>
                        </Box>

                        <Box className="p-1 mr-2 text-xxs1 text-gray-201">
                          Price Per Share
                        </Box>
                      </VStack>
                      <VStack className="p-2 text-xs border border-dashed rounded xl:min-w-36 lg:min-w-28 md:min-w-20 sm:min-w-16">
                        <Box className="p-1 pb-0 font-semibold text-black-dark text-xs3">
                          <p>
                            {formatCurrency(
                              showActive
                                ? employeePageDetail?.activeOptionsValue
                                : employeePageDetail?.totalOptionValue,
                              currency
                            )}
                          </p>
                        </Box>
                        <Box className="p-1 mr-2 text-xxs1 text-gray-201">
                          {showActive
                            ? "Active Options Value"
                            : "Total Option Value"}
                        </Box>
                      </VStack>
                      <VStack className="p-2 text-xs border border-dashed rounded xl:min-w-36 lg:min-w-28 md:min-w-20 sm:min-w-16">
                        <Box className="p-1 pb-0 font-semibold text-black-dark text-xs3">
                          <p>
                            {formatCurrency(
                              showActive
                                ? employeePageDetail?.activeOptionsCost
                                : employeePageDetail?.totalOptionCost,
                              currency
                            )}
                          </p>
                        </Box>
                        <Box className="p-1 mr-2 text-xxs1 text-gray-201">
                          {showActive
                            ? "Active Options Cost"
                            : "Total Option Cost"}
                        </Box>
                      </VStack>
                    </Box>
                  </Box>
                ) : (
                  <p></p>
                )}
              </VStack>
            </VStack>
            <VStack className="justify-between gap-4">
              {/* vested factor */}
              <Box className="flex flex-row space-x-4 ">
                <VStack>
                  <HStack className="justify-end gap-4">
                    <ButtonPrimary
                      buttonSize={ButtonSize.SMALL}
                      className={
                        !determineUserAccessToResource(
                          permission?.aclList || [],
                          Resource.Employee,
                          DefaultAction.Create
                        )
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      }
                      disabled={
                        !determineUserAccessToResource(
                          permission?.aclList || [],
                          Resource.Employee,
                          DefaultAction.Create
                        )
                      }
                      onClick={() => {
                        setDialog({
                          open: true,
                          message: `Are you sure you want to generate credentials?`,
                          primaryAction: genCred,
                        });
                      }}
                    >
                      {employeePageDetail?.isLoginProvided !== true
                        ? "Generate Credentials"
                        : "Re-Generate Credentials"}
                    </ButtonPrimary>
                    <Box className="items-center px-2 py-3 bg-gray-300 rounded-sm">
                      <CTADropdown
                        actions={moreActions}
                        onAction={(action) =>
                          handleAction(employeePageDetail, action)
                        }
                      />
                    </Box>
                  </HStack>
                  {employeePageDetail?.isLoginProvided &&
                    employeePageDetail?.lastCredentialsGeneratedDate && (
                      <HStack className="py-2 text-xs text-gray-201">
                        <Box>
                          <div>Generated On : </div>
                        </Box>
                        <Box>
                          {formatDisplayDate(
                            employeePageDetail?.lastCredentialsGeneratedDate
                          )}
                        </Box>
                      </HStack>
                    )}
                </VStack>

                <Dialog open={open} maxWidth="md">
                  <AddOrEditGrant
                    onClose={() => setOpen(!open)}
                    grant={undefined}
                    mode={dialog.mode}
                  />
                </Dialog>

                <Dialog open={initiationOpen} maxWidth="md">
                  <InitiateResignation
                    onClose={() => setInitiationOpen(!initiationOpen)}
                    onPrimaryAction={() => {
                      setActiveTab("Resignation");
                      setInitiationOpen(!initiationOpen);
                    }}
                    dateOfJoining={
                      new Date(employeePageDetail?.dateOfJoin || new Date())
                    }
                  />
                </Dialog>
                <Dialog
                  open={dialog.open}
                  onClose={() => setDialog({ open: false })}
                  maxWidth="md"
                >
                  <AlertDialog
                    onClose={() => setDialog({ open: false })}
                    open={dialog.open}
                    message={dialog.message}
                    onPrimaryAction={dialog.primaryAction}
                    onSecondaryAction={() => setDialog({ open: false })}
                    loading={
                      generateCredentialLoading ||
                      deletingEmployee ||
                      resumeVesting ||
                      revokingOrEnablingAccess
                    }
                  />
                </Dialog>
                <Dialog
                  open={employeeAccessDialog.open}
                  onClose={() => setEmployeeAccessDialog({ open: false })}
                  maxWidth="md"
                >
                  <Box className="overflow-x-auto overflow-y-auto p-4 w-full min-w-[600px]">
                    <Box className="sticky py-4 text-lg font-medium border-b">
                      <h6 className="flex justify-between">
                        Transfer employee access
                        <span
                          onClick={() =>
                            setEmployeeAccessDialog({ open: false })
                          }
                          className="cursor-pointer"
                        >
                          X
                        </span>
                      </h6>
                    </Box>
                    <HStack className="justify-between w-full gap-4 mt-4 align-middle">
                      <VStack className="my-auto align-middle">
                        Personal Email :
                      </VStack>
                      <VStack className="w-3/4">
                        <Input
                          value={employeeAccessDialog.personalEmail}
                          type="email"
                          onChange={(e) => {
                            setEmployeeAccessDialog({
                              open: employeeAccessDialog.open,
                              personalEmail: e.target.value,
                            });
                          }}
                        />
                      </VStack>
                    </HStack>
                    <HStack className="justify-end w-full gap-6 my-4">
                      <VStack className="">Regenerate Credentials ?</VStack>
                      <VStack>
                        <SwitchButton
                          onClick={() => {
                            setEmployeeAccessDialog({
                              open: employeeAccessDialog.open,
                              regenerateCredentials:
                                !employeeAccessDialog.regenerateCredentials,
                              personalEmail: employeeAccessDialog.personalEmail,
                            });
                          }}
                          value={
                            employeeAccessDialog.regenerateCredentials || false
                          }
                          label=""
                        />
                      </VStack>
                    </HStack>
                    <p className="mt-16 italic text-xxs">{`Note: This action will transfer employee's access to his personal email.`}</p>
                    <HStack className="justify-end w-full gap-4 mt-4">
                      <ButtonPrimary1
                        onClick={() => {
                          setEmployeeAccessDialog({ open: false });
                        }}
                      >
                        Cancel
                      </ButtonPrimary1>
                      <ButtonPrimary
                        buttonSize={ButtonSize.SMALL}
                        loading={transferringAccess || false}
                        disabled={
                          employeeAccessDialog.personalEmail?.length === 0
                        }
                        onClick={() => {
                          transferAccess(
                            {
                              employeeId: employeePageDetail.id,
                              personalEmail:
                                employeeAccessDialog?.personalEmail,
                              regenerateCredentials:
                                employeeAccessDialog.regenerateCredentials,
                            },
                            {
                              onSuccess: () => {
                                toast("Account access has been transferred", {
                                  autoClose: 2000,
                                  type: "success",
                                });
                                setEmployeeAccessDialog({ open: false });
                              },
                              onError: (e) => {
                                toast("Failed to transfer account", {
                                  autoClose: 2000,
                                  type: "error",
                                });
                              },
                            }
                          );
                        }}
                      >
                        Confirm
                      </ButtonPrimary>
                    </HStack>
                  </Box>
                </Dialog>
                <RevertResignationDialog
                  open={revertResignationDialog.open}
                  onClose={() =>
                    setRevertResignationDialog((prev) => ({
                      ...prev,
                      open: false,
                    }))
                  }
                  revertResignation={revertResignationDialog.revertResignation}
                  isRevertResignation={true}
                  onSecondaryAction={() =>
                    setRevertResignationDialog((prev) => ({
                      ...prev,
                      open: false,
                    }))
                  }
                />
                <EmployeePortalPreview
                  open={employeeDetailsDialog.open}
                  onClose={closeEmployeePreview}
                  iframeRef={employeePreviewFrame}
                  accessToken={employeeDetailsDialog.previewAccessToken}
                />
              </Box>
              {(employeeGrantedOption || 0) > 0 ? (
                <Box>
                  <VStack className="p-2 min-w-min">
                    <Box className="flex flex-row justify-between ">
                      <p className="text-stone-300 text-xs4 ">Vested factor</p>
                      <p className="text-black text-xs4">
                        {(vestedFactor * 100).toFixed()}
                        {" %"}
                      </p>
                    </Box>
                    <BorderLinearProgress
                      className=""
                      variant="determinate"
                      value={vestedFactor * 100}
                    />
                  </VStack>
                </Box>
              ) : (
                ""
              )}
            </VStack>
          </HStack>
        </Box>
      </Box>
      <HStack className="gap-8 mb-0 flex-center">
        {tabs.map((item, i) => (
          <Box
            key={i}
            className={`text-xs3 font-semibold cursor-pointer py-4 ${
              item === activeTab
                ? "border-b-2 border-solid border-red-500 text-red-500"
                : "text-gray-201 "
            }`}
            {...rest}
          >
            {item}
          </Box>
        ))}
      </HStack>
    </Box>
  );
};

export default EmployeeHeader;
